import { 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail,
  updateProfile,
  multiFactor,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  getMultiFactorResolver
} from 'firebase/auth';
import { 
  doc, 
  setDoc, 
  getDoc, 
  collection, 
  addDoc, 
  serverTimestamp, 
  query,
  where,
  getDocs,
  updateDoc,
  arrayUnion,
  arrayRemove,
  orderBy
} from 'firebase/firestore';
import { auth, db, collections, ROLES } from '../firebase.marketplace';

/**
 * Marketplace Authentication Service
 * 
 * This service provides authentication functions for the marketplace
 * with proper error handling and consistent behavior
 */

// Sign up with email/password
export const signUp = async (email, password, displayName) => {
  try {
    // Create user with Firebase Auth
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Update profile with display name
    await updateProfile(user, { displayName });
    
    // Create user document in Firestore
    await createUserDocument(user, { displayName });
    
    return user;
  } catch (error) {
    console.error('Sign up error:', error);
    
    // Provide user-friendly error messages
    if (error.code === 'auth/email-already-in-use') {
      throw new Error('This email is already registered. Please sign in instead.');
    } else if (error.code === 'auth/weak-password') {
      throw new Error('Password is too weak. Please use at least 6 characters.');
    } else {
      throw new Error(error.message || 'Failed to create account');
    }
  }
};

// Sign in with email/password
export const signIn = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    try {
      // Check if user document exists, create if not
      const userDoc = await getDoc(doc(db, collections.USERS, user.uid));
      if (!userDoc.exists()) {
        await createUserDocument(user);
      }
      
      // Record the login session
      await recordLoginSession(user.uid);
    } catch (firestoreError) {
      // If there's a Firestore error, log it but don't fail the sign-in
      console.warn('Warning: Could not access or create user document:', firestoreError);
      // Continue with authentication even if Firestore access fails
    }
    
    return user;
  } catch (error) {
    console.error('Sign in error:', error);
    
    // Handle MFA required error
    if (error.code === 'auth/multi-factor-auth-required') {
      // Get the resolver
      const resolver = getMultiFactorResolver(auth, error);
      
      // Return the resolver for the MFA verification UI
      return { requiresMFA: true, resolver };
    }
    
    // Provide user-friendly error messages
    if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
      throw new Error('Invalid email or password');
    } else if (error.code === 'auth/too-many-requests') {
      throw new Error('Too many failed login attempts. Please try again later or reset your password.');
    } else {
      throw new Error(error.message || 'Failed to sign in');
    }
  }
};

// Sign in with Google
export const signInWithGoogle = async () => {
  try {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    const user = result.user;
    
    try {
      // Check if user document exists, create if not
      const userDoc = await getDoc(doc(db, collections.USERS, user.uid));
      if (!userDoc.exists()) {
        await createUserDocument(user);
      }
    } catch (firestoreError) {
      // If there's a Firestore error, log it but don't fail the sign-in
      console.warn('Warning: Could not access or create user document:', firestoreError);
      // Continue with authentication even if Firestore access fails
    }
    
    return user;
  } catch (error) {
    console.error('Google sign in error:', error);
    throw new Error(error.message || 'Failed to sign in with Google');
  }
};

// Sign out
export const signOutUser = async () => {
  try {
    await firebaseSignOut(auth);
  } catch (error) {
    console.error('Sign out error:', error);
    throw new Error(error.message || 'Failed to sign out');
  }
};

// Reset password
export const resetPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error) {
    console.error('Reset password error:', error);
    
    if (error.code === 'auth/user-not-found') {
      throw new Error('No account found with this email');
    } else {
      throw new Error(error.message || 'Failed to send password reset email');
    }
  }
};

// Create user document in Firestore
export const createUserDocument = async (user, additionalData = {}) => {
  if (!user) return;
  
  try {
    const userRef = doc(db, collections.USERS, user.uid);
    const snapshot = await getDoc(userRef);
    
    if (!snapshot.exists()) {
      const { email, displayName, photoURL } = user;
      const createdAt = serverTimestamp();
      
      // Default roles for new users
      const roles = { [ROLES.USER]: true };
      
      await setDoc(userRef, {
        uid: user.uid,
        email,
        displayName: displayName || additionalData.displayName || email.split('@')[0],
        photoURL: photoURL || null,
        createdAt,
        roles,
        ...additionalData
      });
      
      // Create a cart for the user
      await setDoc(doc(db, collections.CARTS, user.uid), {
        userId: user.uid,
        items: [],
        createdAt,
        updatedAt: createdAt
      });
      
      // Create a wishlist for the user
      await setDoc(doc(db, collections.WISHLISTS, user.uid), {
        userId: user.uid,
        items: [],
        createdAt,
        updatedAt: createdAt
      });
      
      return userRef;
    } else {
      // Update existing user document if needed
      if (additionalData && Object.keys(additionalData).length > 0) {
        await updateDoc(userRef, {
          ...additionalData,
          updatedAt: serverTimestamp()
        });
      }
      
      return userRef;
    }
  } catch (error) {
    console.error('Error creating user document:', error);
    throw error;
  }
};

// Get user document
export const getUserDocument = async (uid) => {
  if (!uid) return null;
  
  try {
    const userDoc = await getDoc(doc(db, collections.USERS, uid));
    
    if (userDoc.exists()) {
      return {
        id: userDoc.id,
        ...userDoc.data()
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching user document:', error);
    return null;
  }
};

// Get user roles
export const getUserRoles = async (uid) => {
  try {
    const userDoc = await getDoc(doc(db, collections.USERS, uid));
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      
      if (userData.roles) {
        return userData.roles;
      }
      
      if (userData.isAdmin) {
        return {
          [ROLES.USER]: true,
          [ROLES.ADMIN]: true
        };
      }
      
      return { [ROLES.USER]: true };
    }
    
    return { [ROLES.USER]: true };
  } catch (error) {
    console.error('Error fetching user roles:', error);
    return { [ROLES.USER]: true };
  }
};

/**
 * Check if a user has a specific role
 * @param {string} uid - User ID
 * @param {string} role - Role to check
 * @returns {Promise<boolean>} - Whether user has the role
 */
export const hasRole = async (uid, role) => {
  try {
    const roles = await getUserRoles(uid);
    return roles[role] === true;
  } catch (error) {
    console.error('Error checking role:', error);
    return false;
  }
};

/**
 * Request artist role for a user
 * @param {string} uid - User ID
 * @returns {Promise<void>}
 */
export const requestArtistRole = async (uid) => {
  try {
    // Create an artist request document
    await setDoc(doc(db, collections.ARTIST_REQUESTS, uid), {
      userId: uid,
      status: 'pending',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    // Update user document with pending artist request
    await updateDoc(doc(db, collections.USERS, uid), {
      artistRequestStatus: 'pending',
      updatedAt: serverTimestamp()
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error requesting artist role:', error);
    throw error;
  }
};

/**
 * Approve or reject an artist role request
 * @param {string} requestId - Request ID (same as user ID)
 * @param {string} status - 'approved' or 'rejected'
 * @param {string} reviewerId - ID of the super admin who reviewed the request
 * @param {string} comments - Optional comments from the reviewer
 * @returns {Promise<void>}
 */
export const processArtistRequest = async (requestId, status, reviewerId, comments = '') => {
  try {
    // Check if reviewer is a super admin
    const isSuperAdmin = await hasRole(reviewerId, ROLES.SUPER_ADMIN);
    if (!isSuperAdmin) {
      throw new Error('Only super admins can approve artist requests');
    }
    
    // Update the request document
    await updateDoc(doc(db, collections.ARTIST_REQUESTS, requestId), {
      status: status,
      reviewerId: reviewerId,
      reviewedAt: serverTimestamp(),
      comments: comments,
      updatedAt: serverTimestamp()
    });
    
    // Update the user's roles if approved
    if (status === 'approved') {
      const userRef = doc(db, collections.USERS, requestId);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const currentRoles = userData.roles || { [ROLES.USER]: true };
        
        await updateDoc(userRef, {
          roles: { ...currentRoles, [ROLES.ARTIST]: true },
          artistRequestStatus: 'approved',
          updatedAt: serverTimestamp()
        });
      }
    } else {
      // Update user document with rejected status
      await updateDoc(doc(db, collections.USERS, requestId), {
        artistRequestStatus: 'rejected',
        updatedAt: serverTimestamp()
      });
    }
    
    return { success: true };
  } catch (error) {
    console.error('Error processing artist request:', error);
    throw error;
  }
};

/**
 * Get all pending artist requests
 * @returns {Promise<Array>} - List of artist requests
 */
export const getPendingArtistRequests = async () => {
  try {
    const q = query(
      collection(db, collections.ARTIST_REQUESTS),
      where('status', '==', 'pending'),
      orderBy('createdAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const requests = [];
    
    for (const doc of querySnapshot.docs) {
      const requestData = doc.data();
      
      // Get user details
      const userDoc = await getDoc(doc(db, collections.USERS, requestData.userId));
      const userData = userDoc.exists() ? userDoc.data() : {};
      
      requests.push({
        id: doc.id,
        ...requestData,
        user: {
          displayName: userData.displayName || 'Unknown User',
          email: userData.email || 'No Email',
          photoURL: userData.photoURL || null
        }
      });
    }
    
    return requests;
  } catch (error) {
    console.error('Error getting artist requests:', error);
    return [];
  }
};

/**
 * Get artist request status for a user
 * @param {string} uid - User ID
 * @returns {Promise<string|null>} - Request status or null if no request exists
 */
export const getArtistRequestStatus = async (uid) => {
  try {
    const userDoc = await getDoc(doc(db, collections.USERS, uid));
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      return userData.artistRequestStatus || null;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting artist request status:', error);
    return null;
  }
};

/**
 * Check if user has a pending artist request
 * @param {string} uid - User ID
 * @returns {Promise<Object|null>} - Request data or null
 */
export const checkArtistRequest = async (uid) => {
  if (!uid) return null;
  
  try {
    const requestDoc = await getDoc(doc(db, collections.ARTIST_REQUESTS, uid));
    
    if (requestDoc.exists()) {
      return {
        id: requestDoc.id,
        ...requestDoc.data()
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error checking artist request:', error);
    return null;
  }
};

// MFA RELATED FUNCTIONS

/**
 * Enrolls a user in multi-factor authentication with phone
 * @param {Object} user - Firebase user object
 * @param {string} phoneNumber - Phone number for MFA
 * @param {Object} recaptchaVerifier - reCAPTCHA verifier instance
 * @returns {Promise<Object>} - MFA enrollment result
 */
export const enrollMFA = async (user, phoneNumber, recaptchaVerifier) => {
  try {
    // Get the multiFactor object for the user
    const multiFactorUser = multiFactor(user);
    
    // Start the enrollment process
    const phoneAuthProvider = new PhoneAuthProvider(auth);
    const verificationId = await phoneAuthProvider.verifyPhoneNumber(
      phoneNumber, 
      recaptchaVerifier
    );
    
    // Update the user's profile to store the phone number
    await updateDoc(doc(db, collections.USERS, user.uid), {
      mfaPhoneNumber: phoneNumber,
      mfaEnabled: true,
      updatedAt: serverTimestamp()
    });
    
    return { verificationId, phoneNumber };
  } catch (error) {
    console.error('Error enrolling in MFA:', error);
    throw new Error(error.message || 'Failed to set up two-factor authentication');
  }
};

/**
 * Completes MFA enrollment with verification code
 * @param {string} verificationId - Verification ID from enrollMFA
 * @param {string} verificationCode - Code received via SMS
 * @returns {Promise<void>}
 */
export const completeMFAEnrollment = async (user, verificationId, verificationCode) => {
  try {
    const multiFactorUser = multiFactor(user);
    const phoneAuthCredential = PhoneAuthProvider.credential(verificationId, verificationCode);
    const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(phoneAuthCredential);
    
    // Complete enrollment
    await multiFactorUser.enroll(multiFactorAssertion, "Phone number verification");
    
    // Update user document to reflect MFA status
    await updateDoc(doc(db, collections.USERS, user.uid), {
      mfaEnrolled: true,
      updatedAt: serverTimestamp()
    });
    
    return true;
  } catch (error) {
    console.error('Error completing MFA enrollment:', error);
    throw new Error(error.message || 'Failed to complete two-factor authentication setup');
  }
};

/**
 * Verifies MFA code during sign-in
 * @param {Object} resolver - MFA resolver from sign-in attempt
 * @param {string} verificationCode - Code received via SMS
 * @returns {Promise<Object>} - User credential
 */
export const verifyMFACode = async (resolver, verificationCode) => {
  try {
    // Get the phone authentication credential
    const phoneAuthCredential = PhoneAuthProvider.credential(
      resolver.hints[0].uid, 
      verificationCode
    );
    
    // Create multi-factor assertion
    const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(phoneAuthCredential);
    
    // Resolve sign-in
    const userCredential = await resolver.resolveSignIn(multiFactorAssertion);
    
    // Record login session
    await recordLoginSession(userCredential.user.uid);
    
    return userCredential.user;
  } catch (error) {
    console.error('Error verifying MFA code:', error);
    throw new Error(error.message || 'Invalid verification code');
  }
};

/**
 * Creates a reCAPTCHA verifier instance
 * @param {string} containerId - ID of the container element
 * @returns {RecaptchaVerifier} - reCAPTCHA verifier instance
 */
export const createRecaptchaVerifier = (containerId) => {
  return new RecaptchaVerifier(auth, containerId, {
    size: 'normal',
    callback: () => {
      // reCAPTCHA solved, allow the user to continue
    },
    'expired-callback': () => {
      // Reset the reCAPTCHA
    }
  });
};

/**
 * Disables MFA for a user
 * @param {Object} user - Firebase user object
 * @returns {Promise<boolean>} - Success status
 */
export const disableMFA = async (user) => {
  try {
    const multiFactorUser = multiFactor(user);
    
    // Get enrolled factors
    const enrolledFactors = multiFactorUser.enrolledFactors;
    
    if (enrolledFactors.length > 0) {
      // Unenroll each factor
      for (const factor of enrolledFactors) {
        await multiFactorUser.unenroll(factor);
      }
      
      // Update user document
      await updateDoc(doc(db, collections.USERS, user.uid), {
        mfaEnabled: false,
        mfaEnrolled: false,
        updatedAt: serverTimestamp()
      });
      
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Error disabling MFA:', error);
    throw new Error(error.message || 'Failed to disable two-factor authentication');
  }
};

/**
 * Gets all active sessions for a user
 * @param {string} uid - User ID
 * @returns {Promise<Array>} - List of active sessions
 */
export const getUserSessions = async (uid) => {
  try {
    const userDoc = await getDoc(doc(db, collections.USERS, uid));
    
    if (userDoc.exists() && userDoc.data().sessions) {
      return userDoc.data().sessions.filter(session => session.active);
    }
    
    return [];
  } catch (error) {
    console.error('Error getting user sessions:', error);
    throw new Error('Failed to retrieve session information');
  }
};

/**
 * Records a new login session for the user
 * @param {string} uid - User ID
 * @returns {Promise<string>} - Session ID
 */
export const recordLoginSession = async (uid) => {
  try {
    // Get user's IP and device info (in a real app, you'd use a service for this)
    const sessionInfo = {
      id: generateSessionId(),
      timestamp: serverTimestamp(),
      device: getBrowserInfo(),
      ipAddress: 'IP not collected', // In a real app, you'd get this from the server
      active: true
    };
    
    // Add to sessions collection
    const sessionsRef = doc(db, collections.USERS, uid);
    await updateDoc(sessionsRef, {
      sessions: arrayUnion(sessionInfo),
      lastLoginAt: serverTimestamp()
    });
    
    return sessionInfo.id;
  } catch (error) {
    console.error('Error recording session:', error);
    // Don't throw - this shouldn't block login
    return null;
  }
};

/**
 * @param {string} sessionId - Session ID to terminate
 * @returns {Promise<boolean>} - Success status
 */
export const terminateSession = async (uid, sessionId) => {
  try {
    // Get current sessions
    const userDoc = await getDoc(doc(db, collections.USERS, uid));
    
    if (!userDoc.exists() || !userDoc.data().sessions) {
      return false;
    }
    
    const sessions = userDoc.data().sessions;
    const sessionToUpdate = sessions.find(s => s.id === sessionId);
    
    if (!sessionToUpdate) {
      return false;
    }
    
    // Remove the old session and add an updated one
    await updateDoc(doc(db, collections.USERS, uid), {
      sessions: arrayRemove(sessionToUpdate)
    });
    
    // Mark as inactive and add back
    sessionToUpdate.active = false;
    sessionToUpdate.terminatedAt = serverTimestamp();
    
    await updateDoc(doc(db, collections.USERS, uid), {
      sessions: arrayUnion(sessionToUpdate)
    });
    
    return true;
  } catch (error) {
    console.error('Error terminating session:', error);
    throw new Error('Failed to terminate session');
  }
};

// HELPER FUNCTIONS

/**
 * Generates a unique session ID
 * @returns {string} - Session ID
 */
const generateSessionId = () => {
  return Math.random().toString(36).substring(2, 15) + 
         Math.random().toString(36).substring(2, 15);
};

/**
 * Gets browser and device information
 * @returns {Object} - Browser and device info
 */
const getBrowserInfo = () => {
  const userAgent = navigator.userAgent;
  const browserInfo = {
    userAgent,
    browser: 'Unknown',
    device: 'Unknown',
    os: 'Unknown'
  };
  
  // Parse browser
  if (userAgent.indexOf('Firefox') > -1) {
    browserInfo.browser = 'Firefox';
  } else if (userAgent.indexOf('Chrome') > -1) {
    browserInfo.browser = 'Chrome';
  } else if (userAgent.indexOf('Safari') > -1) {
    browserInfo.browser = 'Safari';
  } else if (userAgent.indexOf('Edge') > -1) {
    browserInfo.browser = 'Edge';
  } else if (userAgent.indexOf('MSIE') > -1 || userAgent.indexOf('Trident/') > -1) {
    browserInfo.browser = 'Internet Explorer';
  }
  
  // Parse OS
  if (userAgent.indexOf('Windows') > -1) {
    browserInfo.os = 'Windows';
  } else if (userAgent.indexOf('Mac') > -1) {
    browserInfo.os = 'MacOS';
  } else if (userAgent.indexOf('Linux') > -1) {
    browserInfo.os = 'Linux';
  } else if (userAgent.indexOf('Android') > -1) {
    browserInfo.os = 'Android';
  } else if (userAgent.indexOf('iOS') > -1 || userAgent.indexOf('iPhone') > -1 || userAgent.indexOf('iPad') > -1) {
    browserInfo.os = 'iOS';
  }
  
  // Parse device type
  if (userAgent.indexOf('Mobile') > -1) {
    browserInfo.device = 'Mobile';
  } else if (userAgent.indexOf('Tablet') > -1) {
    browserInfo.device = 'Tablet';
  } else {
    browserInfo.device = 'Desktop';
  }
  
  return browserInfo;
};
